import throttle from 'lodash/throttle';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {checkIfDialogVisible} from '../../../../../../common/checkIfDialogVisible';
import {t} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {preventDefaultEvent} from '../../../../../../common/shared/utils/preventDefaultEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {getInternalAssetImage} from '../../../../../../design-system/atoms/StaticImage/getInternalAssetImage.function';
import {STATIC_IMAGE_INFO_MAP} from '../../../../../../design-system/atoms/StaticImage/StaticImageInfoMap.constant';
import {Text} from '../../../../../../design-system/atoms/Text/Text';
import {checkIfImagePopupVisible} from '../../../../../../imagePopup/checkIfImagePopupVisible';
import {TEXT_VIEWER_SCROLL_THRESHOLD} from '../constant';
import useScrollViewerWheelEvent from '../hook/useScrollViewerWheelEvent';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';
import {goToNextChapter} from '../state/textViewerSlice';

export function ScrollTextViewerBottomContainer({
  productId,
  isDarkColor,
}: {
  productId: number;
  isDarkColor: boolean;
}) {
  const {isMobile} = requestContext.requestInfo.display;
  const {chapterCount, currentChapterId} = useTypedSelector(state => ({
    chapterCount: state.textViewer.viewData[productId]?.chapterCount,
    currentChapterId: state.textViewer.viewData[productId]?.currentChapterId,
  }));

  const touchStartPositisionY = useRef(0);

  const [reachedEnd, setReachedEnd] = useState(false);
  const reachedEndRef = useRef(false);
  const movedBottomRef = useRef(false);

  const {shouldUseNonstopWatching, handleMoveNextProduct} =
    useViewerMenuHandler({
      isMobile: requestContext.requestInfo.display.isMobile,
    });

  const dispatch = useTypedDispatch();

  const handleMoveNextChapter = useCallback(() => {
    if (
      shouldUseNonstopWatching &&
      chapterCount &&
      currentChapterId === chapterCount - 1
    ) {
      handleMoveNextProduct();
    } else {
      dispatch(goToNextChapter({productId}));
    }
  }, [
    chapterCount,
    currentChapterId,
    dispatch,
    handleMoveNextProduct,
    productId,
    shouldUseNonstopWatching,
  ]);

  const onReleaseHandler = useCallback(() => {
    if (
      movedBottomRef.current &&
      !checkIfDialogVisible() &&
      !checkIfImagePopupVisible()
    ) {
      movedBottomRef.current = false;
      handleMoveNextChapter();
    }
  }, [handleMoveNextChapter]);

  const onMoveHandler = useCallback((event: TouchEvent) => {
    const touchMovePositisionY = event.changedTouches[0].clientY;
    if (
      !movedBottomRef.current &&
      touchStartPositisionY.current >
        touchMovePositisionY + TEXT_VIEWER_SCROLL_THRESHOLD
    ) {
      movedBottomRef.current = true;
    }
  }, []);

  const onPressHandler = useCallback((event: TouchEvent) => {
    touchStartPositisionY.current = event.touches[0].clientY;
  }, []);

  const onWheelHandler = useMemo(
    () =>
      throttle(
        (event: WheelEvent) => {
          if (
            event.deltaY > 0 &&
            !checkIfDialogVisible() &&
            !checkIfImagePopupVisible()
          ) {
            handleMoveNextChapter();
          }
        },
        500,
        {
          leading: false,
          trailing: true,
        }
      ),
    [handleMoveNextChapter]
  );

  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      if (
        (event.code === 'ArrowDown' || event.code === 'Down') &&
        !checkIfDialogVisible() &&
        !checkIfImagePopupVisible()
      ) {
        handleMoveNextChapter();
      }
    },
    [handleMoveNextChapter]
  );

  const handleScroll = useMemo(
    () =>
      throttle(() => {
        // 스크롤이 가장 아래로 내려온 경우, reachedEnd 값을 true로 변경합니다.
        if (
          window.innerHeight + Math.ceil(window.pageYOffset + 1) >=
          document.body.scrollHeight
        ) {
          if (!reachedEndRef.current) {
            setReachedEnd(true);
          }
        } else {
          if (reachedEndRef.current && !movedBottomRef.current) {
            setReachedEnd(false);
          }
        }
      }, 100),
    []
  );

  useEffect(() => {
    reachedEndRef.current = reachedEnd;
  }, [reachedEnd]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useScrollViewerWheelEvent({
    onPressHandler,
    onMoveHandler,
    onReleaseHandler,
    onScroll: handleScroll,
    onWheelHandler,
    onKeyDownHandler,
    isReached: reachedEnd,
    isReachedRef: reachedEndRef,
  });

  const downImageInfo = STATIC_IMAGE_INFO_MAP[IconType.ListArrowDown];

  return (
    <div
      className={twMerge(
        'flex w-full justify-center',
        isMobile ? 'h-[60vh] items-end' : 'h-192pxr items-center'
      )}
    >
      <div
        className={twMerge(
          'flex cursor-pointer flex-row items-center justify-center py-2pxr pl-24pxr active:opacity-30',
          isMobile && 'mb-32pxr'
        )}
        onClick={event => {
          event.stopPropagation();
          handleMoveNextChapter();
        }}
      >
        <Text
          className={twMerge(
            'font-medium2',
            isDarkColor ? 'text-static-mono-white' : 'text-static-mono-black'
          )}
        >
          {t('viewer_next_chapter_button')}
        </Text>
        <img
          alt={downImageInfo.alt}
          src={getInternalAssetImage(
            (isDarkColor ? downImageInfo.srcDark : downImageInfo.srcLight) || ''
          )}
          onSelect={preventDefaultEvent}
          onDragStart={preventDefaultEvent}
          onContextMenu={preventDefaultEvent}
        />
      </div>
    </div>
  );
}
